import React from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import { Placement } from '@popperjs/core';
import styled, { keyframes, useTheme } from 'styled-components/macro';
import { SPACINGS } from '../../utils/style';
import { Heading, Paragraph } from '../../typography';

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const PopperWrapper = styled.div`
  opacity: 0;
  animation: ${fadeIn} 0.1s ease-in 0.1s forwards;
  z-index: ${(p) => p.theme.zIndexes.overlay};
`;

const Arrow = styled.div<{ color: 'dark' | 'light' }>`
  position: absolute;
  width: ${SPACINGS[4]};
  height: ${SPACINGS[4]};
  z-index: -1;

  &::before {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    width: ${SPACINGS[4]};
    height: ${SPACINGS[4]};
    z-index: -1;
    content: '';
    transform: rotate(45deg);
    border: 1px solid
      ${(p) =>
        p.color === 'light'
          ? p.theme.colors.secondary90w
          : p.theme.colors.secondary};
    background-color: ${(p) =>
      p.color === 'light' ? p.theme.colors.white : p.theme.colors.secondary};
    z-index: ${(p) => p.theme.zIndexes.overlay};
  }

  &[data-placement*='top'] {
    bottom: -${SPACINGS[2]};
  }

  &[data-placement*='right'] {
    left: -${SPACINGS[2]};
  }

  &[data-placement*='bottom'] {
    top: -${SPACINGS[2]};
  }

  &[data-placement*='left'] {
    right: -${SPACINGS[2]};
  }
`;

export const TooltipWrapper = styled.div<{ color: 'dark' | 'light' }>`
  display: block;
  padding: ${SPACINGS[4]};
  max-width: 490px;
  border-radius: 2px;
  border: 1px solid
    ${(p) =>
      p.color === 'light'
        ? p.theme.colors.secondary90w
        : p.theme.colors.secondary};
  background-color: ${(p) =>
    p.color === 'light' ? p.theme.colors.white : p.theme.colors.secondary};
  text-align: start;
`;

export type TooltipProps = {
  children: React.ReactNode;
  title?: string;
  text?: string | React.ReactNode;
  placement?: Placement;
  color?: 'dark' | 'light';
  containerStyle?: React.CSSProperties;
};

function Tooltip(props: TooltipProps) {
  const {
    title,
    text,
    children,
    placement,
    color = 'dark',
    containerStyle,
  } = props;
  const theme = useTheme();

  const [referenceElement, setReferenceElement] = React.useState(null as any);
  const [popperElement, setPopperElement] = React.useState(null as any);
  const [arrowElement, setArrowElement] = React.useState(null as any);
  const { styles, attributes, update } = usePopper(
    referenceElement,
    popperElement,
    {
      placement: placement ?? 'bottom',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 25],
          },
        },
        { name: 'arrow', options: { element: arrowElement } },
        {
          name: 'flip',
          enabled: placement === 'auto',
        },
        {
          name: 'preventOverflow',
          enabled: true,
        },
        {
          name: 'hide',
          enabled: false,
        },
      ],
    }
  );

  React.useEffect(() => {
    if (update) update();
  }, [text, update]);

  const [onOver, setOnOver] = React.useState(false);

  return (
    <>
      <span
        onMouseOver={() => {
          if (!onOver) setOnOver(true);
        }}
        onMouseLeave={() => {
          if (onOver) setOnOver(false);
        }}
        onFocus={() => {
          if (!onOver) setOnOver(true);
        }}
        ref={setReferenceElement}
        style={containerStyle}
      >
        {children}
      </span>
      {onOver &&
        ReactDOM.createPortal(
          <PopperWrapper
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}
          >
            <TooltipWrapper color={color}>
              {title && (
                <Heading style={{ color: theme.colors.white }} type={5}>
                  {title}
                </Heading>
              )}
              {typeof text === 'string' ? (
                <Paragraph
                  style={{ color: theme.colors.white }}
                  innerHTML={text}
                />
              ) : (
                text
              )}
            </TooltipWrapper>
            <Arrow
              color={color}
              ref={setArrowElement}
              style={styles.arrow}
              data-placement={attributes?.popper?.['data-popper-placement']}
            />
          </PopperWrapper>,
          document.body
        )}
    </>
  );
}

export default Tooltip;
